import type { EdgeEntityData, EdgeEntityDataAttributes } from '@ipfabric/utils';
import { isProtocolStpi } from '../../../helpers/isProtocolStpi';
import type { ProtocolTableFilterColumns } from '../../../protocolTables';

type AttributeFilterOperator = 'eq' | 'like';

export interface AttributeFilter {
  [key: string]:
    | [
        operator: AttributeFilterOperator,
        value: string | number | null | undefined,
      ]
    | [
        objectOperator: 'any',
        column: 'intName' | 'sn',
        operator: string,
        value: string | null | undefined,
      ];
}

const getRef = (ref: 'source' | 'target', useOppositeRefForValue: boolean) => {
  if (!useOppositeRefForValue) {
    return ref;
  }
  return ref === 'source' ? 'target' : 'source';
};

export const createSnFilter =
  (
    ref: 'source' | 'target',
    operator: AttributeFilterOperator = 'eq',
    useOppositeRefForValue = false,
  ) =>
  (
    edge: EdgeEntityData,
    protocolTableFilterColumns: ProtocolTableFilterColumns,
  ): AttributeFilter | null => {
    const valueRef = getRef(ref, useOppositeRefForValue);
    return edge[valueRef]?.sn && protocolTableFilterColumns[ref]?.sn
      ? {
          [protocolTableFilterColumns[ref]!.sn!]: [operator, edge[valueRef].sn],
        }
      : null;
  };

export const createCustomSnFilter =
  (
    ref: 'source' | 'target',
    filterColumnName: string,
    dataColumnName: keyof EdgeEntityDataAttributes,
    operator: AttributeFilterOperator = 'eq',
  ) =>
  (edge: EdgeEntityData): AttributeFilter | null => {
    const data = edge[ref][dataColumnName] as string;
    if (data) {
      return { [filterColumnName]: [operator, data] };
    }
    return null;
  };

export const createInventoryHostsSnFilter =
  (
    ref: 'source' | 'target',
    operator: AttributeFilterOperator = 'eq',
    useOppositeRefForValue = false,
  ) =>
  (edge: EdgeEntityData): AttributeFilter | null => {
    const valueRef = getRef(ref, useOppositeRefForValue);
    return edge[valueRef]?.sn
      ? { edges: ['any', 'sn', operator, edge[valueRef].sn] }
      : null;
  };

export const createIntNameFilter =
  (
    ref: 'source' | 'target',
    operator: AttributeFilterOperator = 'eq',
    useOppositeRefForValue = false,
  ) =>
  (
    edge: EdgeEntityData,
    protocolTableFilterColumns: ProtocolTableFilterColumns,
  ): AttributeFilter | null => {
    const valueRef = getRef(ref, useOppositeRefForValue);
    return edge[valueRef]?.intName && protocolTableFilterColumns[ref]?.intName
      ? {
          [protocolTableFilterColumns[ref]!.intName!]: [
            operator,
            edge[valueRef].intName,
          ],
        }
      : null;
  };

export const createInventoryHostsChildIntNameFilter =
  (ref: 'source' | 'target', operator = 'eq', useOppositeRefForValue = false) =>
  (edge: EdgeEntityData): AttributeFilter | null => {
    const valueRef = getRef(ref, useOppositeRefForValue);
    return edge[valueRef]?.intName
      ? { edges: ['any', 'intName', operator, edge[valueRef].intName] }
      : null;
  };

export const createNexthopFilter =
  (ref: 'source' | 'target', operator = 'eq', useOppositeRefForValue = false) =>
  (
    edge: EdgeEntityData,
    protocolTableFilterColumns: ProtocolTableFilterColumns,
  ): AttributeFilter | null => {
    const valueRef = getRef(ref, useOppositeRefForValue);
    return edge[valueRef]?.intName && protocolTableFilterColumns[ref]?.intName
      ? {
          [protocolTableFilterColumns[ref]!.intName!]: [
            'any',
            'intName',
            operator,
            edge[valueRef].intName,
          ],
        }
      : null;
  };

export const createAddressFilter =
  (
    ref: 'source' | 'target',
    operator: AttributeFilterOperator = 'eq',
    useOppositeRefForValue = false,
  ) =>
  (
    edge: EdgeEntityData,
    protocolTableFilterColumns: ProtocolTableFilterColumns,
  ): AttributeFilter | null => {
    const valueRef = getRef(ref, useOppositeRefForValue);
    return edge[valueRef]?.ipAddress &&
      protocolTableFilterColumns[ref]?.ipAddress
      ? {
          [protocolTableFilterColumns[ref]!.ipAddress!]: [
            operator,
            edge[valueRef].ipAddress,
          ],
        }
      : null;
  };

export const createVlanIdFilter =
  (operator: AttributeFilterOperator = 'eq') =>
  (
    edge: EdgeEntityData,
    protocolTableFilterColumns: ProtocolTableFilterColumns,
  ): AttributeFilter | null =>
    isProtocolStpi(edge?.protocol) &&
    // NOTE: there is a bit weird thing. edge.vlanId===null passes this condition
    // and is sent to filter, but in other methods above, edge.ipAddress and edge.intName
    // excluded from result filter. It seems no one knows how this was meant,
    // after checking with Roman Aprias it is kindof sane - we may filter by null vlanId,
    // but ipAddress and intName should not be null (yep, then it is a question why the type allows it).
    // So it seems this may be correct although not very clearly expressed in code,
    // and I decided to not dig deeper into it while no actual problem was reported here.
    // Leaving a comment just in case someone wonders like me here or as a clue if we
    // stumble into an actual problem here.
    edge?.vlanId !== undefined &&
    protocolTableFilterColumns?.vlanId
      ? { [protocolTableFilterColumns.vlanId]: [operator, edge.vlanId] }
      : null;
